/**
 * インポート画面用「図書館の予約から選択」BottomSheet
 *
 * `library-favorites` のうち `status='reserved'` のみを一覧表示し、選択された 1 冊を
 * 呼び出し側 (`ImportPage`) にコールバックで返す。
 * 図書館で受け取った (手元にある) 本を先頭に出す。
 *
 * ISBN が無いレコードは Amazon URL を組み立てられないため選択不可 (グレーアウト)。
 * インポート済み判定 (バックエンド `/import/check-isbn`) は呼び出し側で行うため、
 * このコンポーネントはあくまで「選択 UI」に徹する。
 */

import { BookOpen, Loader2 } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import type { LibraryFavorite } from '@/api/types/library-favorite'
import { LibraryPickupBadge } from '@/components/shared/library-pickup-badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { cn } from '@/lib/utils'
import { BookCover } from '@/pages/library-search/components/BookCover'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (favorite: LibraryFavorite) => void
  /** 呼び出し側で `checkIsbnImported` 実行中の場合 true。連続タップ防止のため全ボタンを disable する */
  checking?: boolean
}

export const LibraryReservedSheet = ({ open, onOpenChange, onSelect, checking = false }: Props) => {
  const byIsbn = useLibraryFavoritesStore((s) => s.byIsbn)
  const order = useLibraryFavoritesStore((s) => s.order)
  const loading = useLibraryFavoritesStore((s) => s.loading)
  const loadFavorites = useLibraryFavoritesStore((s) => s.loadFavorites)

  // 開くたびに取得し直す。Product を削除しても自炊済み判定 (fav.product) が
  // キャッシュに残り、再インポートを選べなくなるため。
  useEffect(() => {
    if (!open) return
    void loadFavorites()
  }, [open, loadFavorites])

  const reservedList = useMemo<LibraryFavorite[]>(
    () =>
      order
        .map((isbn) => byIsbn[isbn])
        .filter((fav): fav is LibraryFavorite => fav != null && fav.status === 'reserved')
        // 受取済み (手元にある本) を先頭に。sort は安定なので同順位は登録順のまま
        .sort((a, b) => Number(b.pickup_state === 'picked_up') - Number(a.pickup_state === 'picked_up')),
    [order, byIsbn],
  )

  return (
    <BottomSheet title="図書館の予約から選択" open={open} onOpenChange={onOpenChange} snapPoints={[0.9]}>
      <div className="flex flex-col gap-3 px-4 pb-4">
        <p className="text-xs text-muted-foreground">
          予約中の本を選ぶと自動で local ドメイン・ISBN 紐付け・AI 解析まで実行されます。
          既に自炊済みの場合はブロックされます。
        </p>

        {loading && reservedList.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            読み込み中...
          </div>
        )}

        {!loading && reservedList.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
            <BookOpen size={24} className="opacity-40" />
            <p>予約中の本がありません</p>
            <p className="text-xs">図書館検索画面でお気に入りの状態を「予約中」に切り替えてください</p>
          </div>
        )}

        {reservedList.length > 0 && (
          <ul className="flex flex-col divide-y divide-border/50 rounded-md border border-border/60">
            {reservedList.map((fav) => {
              const alreadyImported = fav.product != null
              // ISBN が無い or 既に自炊済み or check_isbn 実行中は選択不可 (連続タップによる race 防止)
              const disabled = !fav.isbn || alreadyImported || checking
              return (
                // ISBN 未取得のレコードや将来の同 ISBN 重複に備えて DB PK を key にする
                <li key={fav.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(fav)}
                    className={cn(
                      'flex w-full items-start gap-3 p-3 text-left transition-colors',
                      disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/40',
                    )}
                  >
                    <BookCover isbn={fav.isbn || null} className="h-16 w-12" />
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <p className="truncate text-sm font-medium">{fav.title || '（タイトル不明）'}</p>
                      {fav.author && <p className="truncate text-xs text-muted-foreground">{fav.author}</p>}
                      <LibraryPickupBadge favorite={fav} className="self-start" />
                      <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                        {fav.isbn && <span>ISBN {fav.isbn}</span>}
                        {alreadyImported && <span className="text-amber-500">自炊済み</span>}
                      </div>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </BottomSheet>
  )
}
